import type { TFunction } from 'i18next';

import type { IGoal } from '@feathr/blackbox';
import { EGoalType } from '@feathr/blackbox';

export interface IGoalTypeOption {
  id: EGoalType;
  name: string;
  description: string;
}

/**
 * Returns the goal attributes to apply when the user picks a new goal type.
 *
 * Conversion-pixel-trigger goals capture their value from the customer's
 * site code at fire time, so we reset conv_value to 0 instead of the
 * default 5 used for value-bearing goal types.
 *
 * @param newType The goal type the user just selected
 * @returns Partial goal attributes ready for `goal.set(...)`
 */
export function getGoalTypeChangeAttributes(
  newType: EGoalType,
): Pick<IGoal, 'conv_type' | 'segment' | 'multi_conversions' | 'conv_value'> {
  return {
    conv_type: newType,
    segment: undefined,
    multi_conversions: false,
    conv_value: newType === EGoalType.conversion_pixel ? 0 : 5,
  };
}

export const getGoalTypeOptions = (t: TFunction): IGoalTypeOption[] => [
  {
    id: EGoalType.page_view,
    name: t('URL Visit'),
    description: t('Track when a person visits a specific URL'),
  },
  {
    id: EGoalType.group_entry,
    name: t('Group entry'),
    description: t('Track when a person enters a specific group'),
  },
  {
    id: EGoalType.conversion_pixel,
    name: t('Conversion Pixel Trigger'),
    description: t('Track when a conversion pixel is triggered'),
  },
  {
    id: EGoalType.form_submission,
    name: t('Form Submission'),
    description: t('Track when a person submits a form'),
  },
  {
    id: EGoalType.donation_form_submission,
    name: t('Donation Form Submission'),
    description: t('Track when a person submits a donation form'),
  },
];
